import { useState, useEffect, useRef } from 'react';
import { renderAndDownloadPDF } from '#client/pages/Voucher/voucherPDF';
import { useParams, useNavigate, useLocation } from 'react-router';
import {
    Page,
    Layout,
    Card,
    Text,
    TextField,
    Grid,
    Button,
    IndexTable,
    DropZone,
    InlineStack,
    Tabs,
    Icon,
    Box,
    Select,
    SkeletonPage,
    SkeletonBodyText,
    SkeletonDisplayText,
    RadioButton,
    ProgressBar,
    Modal,
} from '@shopify/polaris';
import { EmailIcon, ArrowDownIcon } from '@shopify/polaris-icons';
import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import Papa from 'papaparse';
import './VoucherDistribution.css';
import Breadcrumb from '#client/components/common/Breadcrumb/Breadcrumb';
import DatePicker from '#client/pages/Programs/Program/components/ProgramInfo/DatePicker';
import { useToast } from '#client/components/common/Toast/ToastContext';
import ConfirmActiveVouchersModal from './ConfirmActiveVouchersModal.jsx';
import { voucherService } from '#client/services/programs/programServices';

const baseSchema = z.object({
    startDate: z.date(),
    expirationDate: z.date(),
    distribution: z.string(),
});

function getSchema(tab, distribution) {
    return baseSchema
        .extend({
            firstName: z.string().optional(),
            lastName: z.string().optional(),
            email: z.string().optional(),
        })
        .superRefine((data, ctx) => {
            if (tab === 0 && distribution === 'Email') {
                // Use Zod to validate required + format
                const emailResult = z.string().min(1, 'Email is required for email distribution.').email('Invalid email address.').safeParse(data.email);
                // If email validation fails, add issues to context
                if (!emailResult.success) {
                    for (const issue of emailResult.error.issues) {
                        ctx.addIssue({
                            path: ['email'],
                            code: issue.code,
                            message: issue.message,
                        });
                    }
                }
                if (!data.firstName || !data.firstName.trim()) {
                    ctx.addIssue({
                        path: ['firstName'],
                        code: z.ZodIssueCode.custom,
                        message: 'First name is required for email distribution.',
                    });
                }
                if (!data.lastName || !data.lastName.trim()) {
                    ctx.addIssue({
                        path: ['lastName'],
                        code: z.ZodIssueCode.custom,
                        message: 'Last name is required for email distribution.',
                    });
                }
            }
        });
}

// Zod date-only schema used by BOTH submits
const dateRulesSchema = z
    .object({
        programStart: z.date(),
        startDate: z.date(),
        expirationDate: z.date(),
    })
    .superRefine(({ programStart, startDate, expirationDate }, ctx) => {
        if (startDate < programStart) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['startDate'],
                message: "Start date can't be before the program begins.",
            });
        }
        if (expirationDate <= startDate) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['expirationDate'],
                message: 'Expiration date must be after the start date.',
            });
        }
    });

function ManualVoucherDistribution() {
    const { addToast } = useToast();
    const maxBulkVouchers = 1000;
    const today = new Date();
    const fileInputRef = useRef(null);
    const pdfAbortControllerRef = useRef(null);
    const createdVoucherIdsRef = useRef([]);
    const navigate = useNavigate();
    const { id: programId } = useParams();
    const { pathname } = useLocation();
    const isCustomer = pathname.startsWith('/customer');

    const [tabIndex, setTabIndex] = useState(0);
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [distribution, setDistribution] = useState('Download');
    const [startDate, setStartDate] = useState(today);
    const [expirationDate, setExpirationDate] = useState(today);
    const [csvData, setCsvData] = useState([]);
    const [numAnonymous, setNumAnonymous] = useState(1);
    const [bulkMode, setBulkMode] = useState('file');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pdfProgress, setPdfProgress] = useState({ current: 0, total: 0 });
    const [activeModal, setActiveModal] = useState({
        open: false,
        duplicateEmails: [],
        activeEmails: [],
        onConfirm: null,
    });

    // helper to open the active vouchers modal with props
    function openActiveModal({ duplicateEmails = [], activeEmails = [], onConfirm }) {
        setActiveModal({
            open: true,
            duplicateEmails,
            activeEmails,
            onConfirm,
        });
    }

    // helper: canonicalize for duplicate detection
    function normalizeForDuplicateCheck(email) {
        return String(email || '')
            .replace(/\uFEFF/g, '') // strip BOM
            .trim()
            .toLowerCase();
    }

    const { data, isLoading: isProgramLoading } = useQuery({
        queryKey: ['vouchers', `program-${programId}`, isCustomer ? 'customer' : 'admin'],
        queryFn: async () => voucherService.fetchVouchersWithProgram(programId, isCustomer),
        enabled: !!programId,
    });

    console.log('Fetched program data:', data);
    const program = data?.program || null;

    const createSingleVoucherMutation = useMutation({
        mutationFn: async (payload) => voucherService.createSingleVoucher(program.id, payload, isCustomer),
        retry: 2,
        onError: () => {
            addToast({ type: 'error', message: 'An error occurred while submitting the voucher.' });
        },
    });

    const createBulkVouchersMutation = useMutation({
        mutationFn: async (expandedVouchers) => voucherService.createBulkVouchers(program.id, expandedVouchers, isCustomer),
        retry: 2,
        onError: (err) => {
            const serverMessage = err.response?.data?.error || err.response?.data?.message || JSON.stringify(err.response?.data);
            addToast({ type: 'error', message: serverMessage || 'An error occurred during bulk upload.' });
        },
    });

    // Function to create a single voucher
    async function createSingleVoucher(payload) {
        try {
            const response = await createSingleVoucherMutation.mutateAsync(payload);

            if (response.success) {
                const { voucher } = response.data;
                if (!voucher) {
                    addToast({ type: 'error', message: 'No voucher returned from server.' });
                    return;
                }

                // attach program so the PDF has program info
                if (program?.id) voucher.program = program;

                // handle email failure vs success
                if (voucher.emailStatus === 'failed') {
                    addToast({ type: 'error', message: 'Voucher created but the email failed to send.' });
                } else {
                    addToast({ type: 'success', message: 'Voucher created successfully!' });
                }

                if (distribution === 'Download') {
                    await renderAndDownloadPDF([voucher]);
                }

                // reset inputs
                setFirstName('');
                setLastName('');
                setEmail('');
            } else {
                addToast({ type: 'error', message: 'Failed to submit voucher.' });
            }
        } catch (error) {
            return;
        }
    }

    // Function to create bulk vouchers
    async function createBulkVouchers(expandedVouchers) {
        try {
            const response = await createBulkVouchersMutation.mutateAsync(expandedVouchers);

            if (!response.success) {
                const errorMessage = response.error?.message || 'Bulk voucher submission failed.';
                addToast({ type: 'error', message: errorMessage });
                return;
            }

            const vouchers = response.data?.results;

            if (!Array.isArray(vouchers) || vouchers.length === 0) {
                addToast({ type: 'error', message: 'No valid vouchers returned from server.' });
                return;
            }

            // attach program to each for rendering
            const vouchersWithProgram = vouchers.map((v) => ({ ...v, program }));

            if (distribution === 'Download') {
                // Store voucher IDs for potential rollback if cancelled
                createdVoucherIdsRef.current = vouchers.map((v) => v.id);

                // Create AbortController for cancellation support
                pdfAbortControllerRef.current = new AbortController();
                try {
                    await renderAndDownloadPDF(vouchersWithProgram, {
                        onProgress: (current, total) => setPdfProgress({ current, total }),
                        signal: pdfAbortControllerRef.current.signal,
                    });
                    // Clear voucher IDs on success - no rollback needed
                    createdVoucherIdsRef.current = [];
                    addToast({ type: 'success', message: 'Bulk voucher submission successful!' });
                } catch (err) {
                    if (err.name === 'AbortError') {
                        // Rollback: delete created vouchers from database
                        if (createdVoucherIdsRef.current.length > 0) {
                            try {
                                await voucherService.bulkDeleteVouchers(program.id, createdVoucherIdsRef.current, isCustomer);
                                addToast({ type: 'info', message: 'PDF generation cancelled. Vouchers have been removed.' });
                            } catch (deleteErr) {
                                console.error('Failed to rollback vouchers:', deleteErr);
                                addToast({ type: 'error', message: 'PDF cancelled but failed to remove vouchers. Please delete them manually.' });
                            }
                            createdVoucherIdsRef.current = [];
                        } else {
                            addToast({ type: 'info', message: 'PDF generation cancelled.' });
                        }
                        return;
                    }
                    throw err;
                } finally {
                    setPdfProgress({ current: 0, total: 0 });
                    pdfAbortControllerRef.current = null;
                }
            } else if (distribution === 'Email') {
                const failed = vouchersWithProgram.filter((v) => v.emailStatus === 'failed');
                if (failed.length > 0) {
                    const msg = `Bulk submission completed, but ${failed.length} email(s) failed to send.`;
                    addToast({ type: 'error', message: msg });
                } else {
                    addToast({ type: 'success', message: 'Bulk vouchers sent successfully via email!' });
                }
            }

            // Reset state on success
            setCsvData([]);
        } catch (err) {
            return;
        }
    }

    function downloadCsvTemplate() {
        const headers = ['email', 'firstName', 'lastName'];
        const exampleRow1 = ['example1@example.com', 'Alice', 'Smith'];
        const exampleRow2 = ['example2@example.com', 'Bob', 'Jones'];
        const rows = [headers, exampleRow1, exampleRow2];

        const csvContent = rows.map((row) => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'voucher-template.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Since new blob URLs are created and not garbage collected, we will revoke the old one to prevent memory leaks
        URL.revokeObjectURL(url);
    }

    const handleCSVUpload = (file) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                if (results.data.length > maxBulkVouchers) {
                    addToast({
                        type: 'error',
                        message: `CSV contains ${results.data.length} rows. Maximum allowed is ${maxBulkVouchers}.`,
                    });
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    return;
                }
                setCsvData(results.data);
                addToast({
                    type: 'success',
                    message: `Parsed ${results.data.length} rows from CSV.`,
                });
                if (fileInputRef.current) fileInputRef.current.value = '';
            },
            error: (err) => {
                console.error('CSV parse error:', err);
                addToast({
                    type: 'error',
                    message: 'Failed to parse CSV file.',
                });
                // reset the input
                if (fileInputRef.current) fileInputRef.current.value = '';
            },
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            handleCSVUpload(file);
        }
    };

    const handleBulkSubmit = async () => {
        if (createBulkVouchersMutation.isPending) return; // guard: prevent double-clicks

        // Validate program exists
        if (program == null || !program.id) {
            addToast({
                type: 'error',
                message: 'Program information is missing.',
            });
            return;
        }

        // Date validation (shared Zod)
        const dateCheck = dateRulesSchema.safeParse({
            programStart: normalizeDate(program.dateStart),
            startDate: normalizeDate(startDate),
            expirationDate: normalizeDate(expirationDate),
        });
        if (!dateCheck.success) {
            const message = dateCheck.error.issues[0]?.message || 'Invalid dates.';
            addToast({
                type: 'error',
                message,
            });
            return;
        }

        // For bulk upload make sure we have csv data
        if (tabIndex === 1 && bulkMode === 'file' && !csvData.length) {
            addToast({
                type: 'error',
                message: 'No CSV data to submit.',
            });
            return;
        }
        // For anonymous vouchers, ensure we have a valid number
        if (tabIndex === 2 && numAnonymous <= 0) {
            addToast({
                type: 'error',
                message: 'Specify number of anonymous vouchers to create.',
            });
            return;
        }

        try {
            const expandedVouchers = [];
            // Block anonymous vouchers from using email
            if (tabIndex === 1 && bulkMode === 'creation' && distribution === 'Email') {
                addToast({ type: 'error', message: 'Anonymous vouchers cannot be sent via email. Please select Download as the distribution method.' });
                return;
            }

            // Validate email fields in file-uploaded CSV
            if (tabIndex === 1 && bulkMode === 'file' && distribution === 'Email') {
                const invalidRows = csvData.filter((row) => !row.email || !row.firstName || !row.lastName);
                if (invalidRows.length > 0) {
                    addToast({ type: 'error', message: 'One or more CSV rows are missing required fields (email, first name, last name).' });
                    return;
                }
            }

            // If using "Bulk Creation", generate anonymous voucher objects with only program and date info.
            // Else, for "File Upload", construct voucher objects using the provided CSV data.
            if (tabIndex === 1 && bulkMode === 'creation' && numAnonymous > 0) {
                for (let i = 0; i < numAnonymous; i++) {
                    expandedVouchers.push({
                        distribution,
                        startDate: startDate.toISOString(),
                        expirationDate: expirationDate.toISOString(),
                        programId: program.id,
                    });
                }
            } else {
                for (const row of csvData) {
                    expandedVouchers.push({
                        firstName: row.firstName,
                        lastName: row.lastName,
                        email: (row.email ?? '')
                            .toString()
                            .replace(/\uFEFF/g, '')
                            .trim()
                            .toLowerCase(),
                        distribution,
                        startDate: startDate.toISOString(),
                        expirationDate: expirationDate.toISOString(),
                        programId: program.id,
                    });
                }
            }
            //adding this validation to guard against empty or invalid csv rows or numAnonymous being 0 or undefined
            if (!expandedVouchers.length) {
                addToast({ type: 'error', message: 'No valid vouchers to submit.' });
                return;
            }

            // bulk pre-checks (only for file upload + email distribution)
            if (tabIndex === 1 && bulkMode === 'file') {
                const counts = new Map();
                const duplicateOriginals = [];
                // find duplicates in uploaded CSV (normalized)
                for (const row of csvData) {
                    const originalEmail = (row.email ?? '').trim();
                    const normalized = normalizeForDuplicateCheck(originalEmail);

                    if (!normalized) continue;

                    const count = counts.get(normalized) || 0;
                    if (count >= 1) {
                        duplicateOriginals.push(originalEmail);
                    }
                    counts.set(normalized, count + 1);
                }

                // deduplicate the duplicates for display
                const duplicateEmails = Array.from(new Set(duplicateOriginals));

                // which emails already have active vouchers
                const uniqueEmailsForCheck = Array.from(new Set(csvData.map((r) => normalizeForDuplicateCheck(r.email)).filter(Boolean)));
                let activeEmails = [];
                if (uniqueEmailsForCheck.length > 0) {
                    const activeCheckResponse = await voucherService.checkActiveVouchers(program.id, uniqueEmailsForCheck, isCustomer);
                    activeEmails = activeCheckResponse?.data?.matchedEmails ?? [];
                }

                // If there are any warnings, open the modal and stop
                const hasAnyWarnings = duplicateEmails.length > 0 || activeEmails.length > 0;
                if (hasAnyWarnings) {
                    openActiveModal({
                        duplicateEmails,
                        activeEmails,
                        onConfirm: async () => {
                            await createBulkVouchers(expandedVouchers);
                        },
                    });
                    return;
                }
            }

            // No warnings → proceed
            await createBulkVouchers(expandedVouchers);
        } catch (err) {
            addToast({
                type: 'error',
                message: err.message || 'An error occurred during bulk upload.',
            });
        }
    };

    const [errors, setErrors] = useState({});

    // Normalize date to resolve time issues for validation
    function normalizeDate(date) {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
    }

    useEffect(() => {
        if (program) {
            if (program.dateStart) setStartDate(normalizeDate(program.dateStart));
            if (program.dateExpires) setExpirationDate(normalizeDate(program.dateExpires));
        }
    }, [program]);

    const tabs = [
        { id: 'single-voucher', content: 'Single Voucher', panelID: 'single-voucher-content' },
        { id: 'bulk-vouchers', content: 'Bulk Vouchers', panelID: 'bulk-vouchers-content' },
    ];

    const handleSubmit = async () => {
        const formSchema = getSchema(tabIndex, distribution);
        const result = formSchema.safeParse({
            email,
            firstName,
            lastName,
            startDate,
            expirationDate,
            distribution,
        });

        if (!result.success) {
            const formatted = result.error.format();
            setErrors({
                email: formatted.email?._errors?.[0],
                firstName: formatted.firstName?._errors?.[0],
                lastName: formatted.lastName?._errors?.[0],
                startDate: formatted.startDate?._errors?.[0],
                expirationDate: formatted.expirationDate?._errors?.[0],
            });
            return;
        }

        // Date validation (shared Zod)
        const dateCheck = dateRulesSchema.safeParse({
            programStart: normalizeDate(program.dateStart),
            startDate: normalizeDate(startDate),
            expirationDate: normalizeDate(expirationDate),
        });
        if (!dateCheck.success) {
            const message = dateCheck.error.issues[0]?.message || 'Invalid dates.';
            addToast({
                type: 'error',
                message,
            });
            return;
        }

        // set error if start date is before program start date
        if (normalizeDate(startDate) < normalizeDate(program.dateStart)) {
            addToast({
                type: 'error',
                message: "Start date can't be before the program begins.",
            });
            return;
        }

        // set error if expiration date is before start date
        if (normalizeDate(expirationDate) <= normalizeDate(startDate)) {
            addToast({
                type: 'error',
                message: 'Expiration date must be after the start date.',
            });
            return;
        }

        const payload = {
            firstName,
            lastName,
            email,
            distribution,
            startDate: startDate.toISOString(),
            expirationDate: expirationDate.toISOString(),
            programId: program.id,
        };

        // ---- SINGLE POST PATH (single) ----
        // Check for active vouchers BEFORE creating; only create once (either after confirm or immediately)
        try {
            if (email?.trim()) {
                const data = await voucherService.checkActiveVouchers(program.id, [email.trim().toLowerCase()], isCustomer);

                const matched = data?.data?.matchedEmails ?? [];
                if (matched.length > 0) {
                    openActiveModal({
                        activeEmails: matched,
                        duplicateEmails: [],
                        onConfirm: async () => {
                            if (isSubmitting) return;
                            setIsSubmitting(true);
                            try {
                                await createSingleVoucher(payload); // single post
                            } finally {
                                setIsSubmitting(false);
                            }
                        },
                    });
                    return;
                }
            }

            if (isSubmitting) return;
            setIsSubmitting(true);
            try {
                // No matches → proceed immediately (single post)
                await createSingleVoucher(payload);
            } finally {
                setIsSubmitting(false);
            }
        } catch (error) {
            console.error('Submit error:', error);
            addToast({
                type: 'error',
                message: 'An error occurred while submitting the voucher.',
            });
        }
    };

    if (isProgramLoading) {
        return (
            <SkeletonPage primaryAction>
                <Layout>
                    <Layout.Section>
                        <SkeletonDisplayText size="medium" />
                        <SkeletonBodyText lines={2} />
                    </Layout.Section>
                </Layout>
            </SkeletonPage>
        );
    }

    if (program == null || !program.id) {
        return <div>Program not found.</div>;
    }

    // Cancel PDF generation
    function handleCancelPdfGeneration() {
        if (pdfAbortControllerRef.current) {
            pdfAbortControllerRef.current.abort();
        }
        setPdfProgress({ current: 0, total: 0 });
    }

    const breadcrumbItems = [
        { label: 'Programs', url: isCustomer ? '/customer/programs' : '/program-management' },
        { label: program?.name || 'Program', url: isCustomer ? `/customer/programs/view/${programId}` : `/program-management/view/${programId}` },
        { label: 'Manage Vouchers', url: isCustomer ? `/customer/voucher-list/${programId}` : `/voucher-list/${programId}` },
        { label: 'New Voucher Creation & Distribution' },
    ];

    return (
        <Box className="voucher-distribution-wrapper">
            <Breadcrumb items={breadcrumbItems} />
            <Page title="New Voucher Creation & Distribution" fullWidth>
                <Layout>
                    <Layout.Section>
                        <Card sectioned>
                            <Text variant="headingMd">Program Name: {program?.name || 'Unknown'}</Text>
                            <Text>Voucher Value: {program?.maxValue ?? 'N/A'}</Text>
                            <Text>Max Items: {program?.maxItems ?? 'N/A'}</Text>
                            <Tabs tabs={tabs} selected={tabIndex} onSelect={setTabIndex} />
                        </Card>
                    </Layout.Section>

                    {tabIndex === 0 && (
                        <Layout.Section>
                            <Card sectioned>
                                <Grid>
                                    <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 4, lg: 4 }}>
                                        <Box paddingInlineStart={200} paddingBlockStart={400}>
                                            <TextField
                                                label="Email Address"
                                                value={email}
                                                onChange={(value) => {
                                                    setEmail(value);
                                                    setErrors((prev) => ({ ...prev, email: undefined }));
                                                }}
                                                type="email"
                                                autoComplete="email"
                                                requiredIndicator={distribution === 'Download' ? false : true}
                                                error={errors.email}
                                            />
                                        </Box>
                                    </Grid.Cell>
                                </Grid>
                                <Grid>
                                    <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 4, lg: 4 }}>
                                        <Box paddingInlineStart={200} paddingBlockStart={400}>
                                            <TextField
                                                label="First Name"
                                                value={firstName}
                                                onChange={(value) => {
                                                    setFirstName(value);
                                                    setErrors((prev) => ({ ...prev, firstName: undefined }));
                                                }}
                                                error={errors.firstName}
                                                requiredIndicator={distribution === 'Download' ? false : true}
                                            />
                                        </Box>
                                    </Grid.Cell>
                                    <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 4, lg: 4 }}>
                                        <Box paddingInlineStart={200} paddingBlockStart={400}>
                                            <TextField
                                                label="Last Name"
                                                value={lastName}
                                                onChange={(value) => {
                                                    setLastName(value);
                                                    setErrors((prev) => ({ ...prev, lastName: undefined }));
                                                }}
                                                error={errors.lastName}
                                                requiredIndicator={distribution === 'Download' ? false : true}
                                            />
                                        </Box>
                                    </Grid.Cell>
                                </Grid>
                                <Box paddingBlockStart="400">
                                    <Text tone="subdued" as="p" variant="bodySm" alignment="start">
                                        *This field group is only required for email distribution.
                                    </Text>
                                </Box>
                            </Card>
                        </Layout.Section>
                    )}

                    {tabIndex === 1 && (
                        <Layout.Section>
                            <Card sectioned>
                                <Box paddingBlockEnd={400}>
                                    <InlineStack gap="200">
                                        <RadioButton label="File Upload" checked={bulkMode === 'file'} onChange={() => setBulkMode('file')} />
                                        <RadioButton label="Bulk Creation" checked={bulkMode === 'creation'} onChange={() => setBulkMode('creation')} />
                                    </InlineStack>
                                </Box>

                                {bulkMode === 'file' && (
                                    <>
                                        <div style={{ marginBottom: '1rem' }}>
                                            <Text variant="headingSm">Upload Voucher CSV</Text>
                                            <Text tone="subdued" as="p" variant="bodySm">
                                                Max {maxBulkVouchers} vouchers per upload.
                                            </Text>
                                        </div>
                                        <Grid>
                                            <Grid.Cell columnSpan={{ xs: 12, md: 6 }}>
                                                <DropZone
                                                    accept="text/csv,.csv,application/vnd.ms-excel,application/x-csv,application/csv,text/x-csv"
                                                    type="file"
                                                    allowMultiple={false}
                                                    onDrop={(acceptedFiles) => handleCSVUpload(acceptedFiles[0])}
                                                >
                                                    <DropZone.FileUpload actionTitle="Add CSV file" />
                                                </DropZone>
                                                <input type="file" accept=".csv" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} />
                                            </Grid.Cell>
                                            <Grid.Cell columnSpan={{ xs: 12, md: 6 }}>
                                                <Box className="button-align-bottom">
                                                    <Button onClick={downloadCsvTemplate}>Download Template</Button>
                                                    <Button
                                                        variant="primary"
                                                        onClick={() => {
                                                            if (fileInputRef.current) fileInputRef.current.value = '';
                                                            fileInputRef.current?.click();
                                                        }}
                                                    >
                                                        Upload
                                                    </Button>
                                                </Box>
                                            </Grid.Cell>
                                        </Grid>

                                        {csvData.length > 0 && (
                                            <div style={{ marginTop: '1rem' }}>
                                                <div style={{ marginBottom: '1rem' }}>
                                                    <Text variant="headingSm" as="h2" paddingBlockStart="400">
                                                        CSV Output ({csvData.length} rows) (Previewing first 10 rows)
                                                    </Text>
                                                </div>
                                                <IndexTable itemCount={csvData.length} headings={[{ title: 'Email' }, { title: 'First Name' }, { title: 'Last Name' }]} selectable={false}>
                                                    {csvData.slice(0, 10).map((item, index) => (
                                                        <IndexTable.Row key={index} position={index}>
                                                            <IndexTable.Cell>{item.email}</IndexTable.Cell>
                                                            <IndexTable.Cell>{item.firstName}</IndexTable.Cell>
                                                            <IndexTable.Cell>{item.lastName}</IndexTable.Cell>
                                                        </IndexTable.Row>
                                                    ))}
                                                </IndexTable>
                                            </div>
                                        )}
                                    </>
                                )}

                                {bulkMode === 'creation' && (
                                    <>
                                        <Text variant="headingSm">Bulk Voucher Creation</Text>
                                        <Text tone="subdued" as="p" variant="bodySm">
                                            Create anonymous vouchers for bulk download, typically for handing out physically.
                                            <br /> (Max {maxBulkVouchers} vouchers per upload)
                                        </Text>
                                        <Box paddingBlockStart={200} width="var(--p-width-3200)">
                                            <TextField
                                                type="number"
                                                label="Number of Vouchers"
                                                value={numAnonymous.toString()}
                                                onChange={(val) => setNumAnonymous(Math.min(parseInt(val) || 0, maxBulkVouchers))}
                                                min={1}
                                                max={maxBulkVouchers}
                                            />
                                        </Box>
                                    </>
                                )}
                            </Card>
                        </Layout.Section>
                    )}

                    <Layout.Section>
                        <Card sectioned>
                            <Grid>
                                {' '}
                                <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 4, lg: 4 }}>
                                    {' '}
                                    <DatePicker label="Start date" value={startDate} onChange={setStartDate} error={errors.startDate} disableDatesBefore={normalizeDate(program.dateStart)} />{' '}
                                </Grid.Cell>{' '}
                                <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 4, lg: 4 }}>
                                    {' '}
                                    <DatePicker label="End date" value={expirationDate} onChange={setExpirationDate} error={errors.expirationDate} disableDatesBefore={startDate} />{' '}
                                </Grid.Cell>{' '}
                            </Grid>
                            <Box paddingInlineStart={200} paddingBlockEnd={400} paddingBlockStart={150}>
                                <Text tone="subdued" as="p" variant="bodySm" alignment="start">
                                    *Adjustable start and end dates are pre-filled based on the program’s schedule dates.
                                    <br />
                                    Note: The start date cannot be set earlier than the program’s start date.
                                </Text>
                            </Box>
                            <Grid>
                                <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 4, lg: 4 }}>
                                    <Box paddingInlineStart={200} paddingBlockEnd={400}>
                                        <Select
                                            label="Distribution Method"
                                            options={[
                                                { label: 'Download', value: 'Download', prefix: <Icon source={ArrowDownIcon} /> },
                                                { label: 'Email', value: 'Email', prefix: <Icon source={EmailIcon} /> },
                                            ]}
                                            value={distribution}
                                            onChange={setDistribution}
                                        />
                                    </Box>
                                </Grid.Cell>
                            </Grid>
                        </Card>
                    </Layout.Section>
                    <Layout.Section>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
                            <Button
                                onClick={() => {
                                    setCsvData([]);
                                    setEmail('');
                                    setFirstName('');
                                    setLastName('');
                                    navigate(isCustomer ? `/customer/programs/view/${programId}` : `/program-management/view/${programId}`);
                                }}
                            >
                                Cancel
                            </Button>

                            <Button
                                variant="primary"
                                onClick={tabIndex === 0 ? handleSubmit : handleBulkSubmit}
                                loading={tabIndex === 0 ? createSingleVoucherMutation.isPending || isSubmitting : createBulkVouchersMutation.isPending}
                            >
                                {distribution === 'Download' ? (tabIndex === 0 ? 'Download Voucher' : 'Download Vouchers') : tabIndex === 0 ? 'Send Voucher' : 'Send Vouchers'}
                            </Button>
                        </div>
                    </Layout.Section>
                </Layout>
            </Page>
            <Modal
                open={pdfProgress.total > 0}
                title="Generating PDF"
                onClose={handleCancelPdfGeneration}
                primaryAction={{
                    content: 'Cancel',
                    destructive: true,
                    onAction: handleCancelPdfGeneration,
                }}
                noScroll
            >
                <Modal.Section>
                    <Box paddingBlockEnd="400">
                        <Text>
                            Processing page {pdfProgress.current} of {pdfProgress.total}...
                        </Text>
                    </Box>
                    <ProgressBar progress={(pdfProgress.current / pdfProgress.total) * 100} size="small" />
                    <Box paddingBlockStart="200">
                        <Text tone="subdued" variant="bodySm">
                            Please wait while vouchers are being rendered.
                            {pdfProgress.total > 50 && ' For large batches, a ZIP file containing multiple PDFs will be downloaded.'}
                        </Text>
                    </Box>
                </Modal.Section>
            </Modal>
            <ConfirmActiveVouchersModal
                open={activeModal.open}
                duplicateEmails={activeModal.duplicateEmails}
                activeEmails={activeModal.activeEmails}
                onClose={() => setActiveModal((current) => ({ ...current, open: false }))}
                onConfirm={() => {
                    const confirmCallback = activeModal.onConfirm;
                    setActiveModal((current) => ({ ...current, open: false }));
                    confirmCallback?.();
                }}
            />
        </Box>
    );
}

export default ManualVoucherDistribution;
